import React, { FC, useEffect, useState } from 'react';
import styles from './_styles.module.scss';
import { useAppDispatch } from '../../core/hooks/reduxHooks';
import { useParams } from 'react-router-dom';
import { setLoading } from '../../redux/reducers/ui/HandlerSlice';
import { getCandidateRatings } from '../../core/services/axios';
import type { CandidateRating as CandidateRatingModel } from '../../core/models/models';
import UIPagination from '../../components/UI/UIPagination/UIPagination';
import { CandidateRating } from '../../components/Candidate/CandidateRating';
import { RatingsCount } from '../../core/models/models';
import { StarIcon, HalfStarIcon, HollowStarIcon } from '../../components/UI/StarIcon/StarIcon';

const initRatingsCount: RatingsCount = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };

const CandidateRatings: FC = () => {
  const { tutorId } = useParams();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageCount, setPageCount] = useState<number>(1);
  const [ratings, setRatings] = useState<CandidateRatingModel[]>([]);
  const [starFilter, setStarFilter] = useState<number | null>(null);
  const [ratingsCount, setRatingsCount] =
    useState<RatingsCount>(initRatingsCount);
  const [averageRating, setAverageRating] = useState<number>(0);
  const dispatch = useAppDispatch();
  const totalCount = Object.values(ratingsCount).reduce((a, b) => a + b, 0);

  useEffect(() => {
    if (tutorId) {
      dispatch(setLoading(true));
      try {
        getCandidateRatings(tutorId, currentPage, starFilter).then(res => {
          const {
            results = [],
            totalPages = 0,
            totals: { count, average },
          } = res;

          setRatings(results);
          setPageCount(totalPages);
          setRatingsCount(count);
          setAverageRating(average);
        });
        dispatch(setLoading(false));
      } catch (error) {
        dispatch(setLoading(false));
      }
    }
  }, [tutorId, currentPage, starFilter]);

  return (
    <section className={styles.candidate_ratings}>
      <h3>Candidate Rating</h3>
      <div className={styles.rating_summary}>
        <div className={styles.rating_overview}>
          <div className={styles.average_rating_container}>
            <span className={styles.average_rating}>
              {averageRating.toFixed(1)}
            </span>{' '}
            <span className={styles.out_of_5}>out of 5</span>
          </div>
          <div className={styles.star_rating}>
            {Array.from({ length: Math.floor(averageRating) }, (_, i) => (
              <span key={`star-${i}`} className={styles.star}>
                <StarIcon />
              </span>
            ))}
            {averageRating % 1 >= 0.5 && (
              <span className={styles.star}>
                <HalfStarIcon />
              </span>
            )}
            {averageRating % 1 < 0.5 && (
              <span className={styles.star}>
                <HollowStarIcon />
              </span>
            )}
            {Array.from({ length: 5 - Math.ceil(averageRating) }, (_, i) => (
              <span
                key={`star-${i + Math.floor(averageRating)}`}
                className={styles.star}
              >
                <HollowStarIcon />
              </span>
            ))}
          </div>
        </div>
        <div className={styles.ratings_count}>
          <button
            onClick={() => setStarFilter(null)}
            data-active={starFilter === null}
          >
            All
          </button>
          <button
            onClick={() => setStarFilter(5)}
            data-active={starFilter === 5}
          >
            5 Star ({ratingsCount[5]})
          </button>
          <button
            onClick={() => setStarFilter(4)}
            data-active={starFilter === 4}
          >
            4 Star ({ratingsCount[4]})
          </button>
          <button
            onClick={() => setStarFilter(3)}
            data-active={starFilter === 3}
          >
            3 Star ({ratingsCount[3]})
          </button>
          <button
            onClick={() => setStarFilter(2)}
            data-active={starFilter === 2}
          >
            2 Star ({ratingsCount[2]})
          </button>
          <button
            onClick={() => setStarFilter(1)}
            data-active={starFilter === 1}
          >
            1 Star ({ratingsCount[1]})
          </button>
        </div>
      </div>
      <div className={styles.ratings_list}>
        {ratings.map((rating, index) => (
          <CandidateRating key={index} rating={rating} />
        ))}
        {totalCount === 0 && (
          <div className={styles.no_ratings}>
            No one has rated this candidate before.
          </div>
        )}
        {totalCount > 0 && ratings.length === 0 && (
          <div className={styles.no_ratings}>No rating available.</div>
        )}
      </div>
      {pageCount > 1 && (
        <UIPagination
          currentPage={currentPage}
          totalPages={pageCount}
          onChangePage={(_, page) => {
            setCurrentPage(page);
          }}
        />
      )}
    </section>
  );
};

export default CandidateRatings;
